<template>
  <PageLayout title="Files" class="relative z-0">
    <template #header>
      <PageHeader v-if="!maxSmall" :title="$t('admin.navigation.files.label')">
        <template #actions>
          <Actions />
        </template>
        <template #views-bar>
          <ViewsBar
            ref="viewsBar"
            type="files"
            :views="views"
            :viewType="'table'"
            :currentView="currentView"
            @selectView="handleSelectView"
            @deleteView="handleDeleteView"
            @addView="handleAddView"
            @renameView="handleRenameView"
            @updateView="handleUpdateView"
          />
        </template>
      </PageHeader>
    </template>
    <FilesOverview
      :count="count"
      :fetchingStatus="fetchingStatus"
      @show:detail="handleShowDetail"
      @update:count="count = $event"
      @reset="handleReset"
      @reload="handleReload"
      @update:fetchingStatus="handleUpdateFetchingStatus"
    />
    <Detail v-if="file" :file="file" @close="handleCloseFileDetails" />
  </PageLayout>
</template>

<script setup>
// Vue and utility imports
import { computed, inject, onUnmounted, ref, watch, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";

// Layout imports
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import PageLayout from "@/Layouts/PageLayout.vue";

// Shared components imports
import { PageHeader } from "@/SharedComponents/PageHeader";
import { ViewsBar } from "@/SharedComponents/PageHeader/Partials";
import { toast } from "@/SharedComponents/Toaster";

// Page specific components imports
import Actions from "@/Pages/Files/Partials/Header/Actions.vue";
import FilesOverview from "./FilesOverview.vue";
import Detail from "@/Pages/Files/Detail.vue";

// Composable imports
import { useFilters } from "@/Composables/useFilters";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useDefaultFilesViews } from "@/Pages/Files/Views";
import { useSites } from "@/Composables/useSites";
import { useFilesViews } from "@/Composables/useFilesViews";
import { useViewControls } from "@/Composables/useViewControls";
import { useI18n } from "vue-i18n";

// Define Options
defineOptions({ layout: PersistentLayout });

// Define Props
defineProps({
  file: { type: Object, required: false },
});

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Translations
const { t } = useI18n();

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// View Controls: Global
const viewControlsStore = useViewControls("viewControls");
const { handleUpdateLoading } = viewControlsStore;

// Views: Files
const { customViews, isLoading } = useFilesViews();
const { defaultViews, DefaultView } = useDefaultFilesViews();

// Filters: Files
const filterStore = useFilters("files");
const { handleInitialiseFilters, handleClearFilters, handleSwitchView } =
  filterStore;

// Breakpoints
const { maxSmall } = useBreakpoints();

// Buildings
const { data: buildings } = useSites();

// Emitter
const emitter = inject("emitter");

// Refs
const viewsBar = ref(null);
const views = ref(defaultViews);
const count = ref(null);
const activeView = ref(DefaultView.ALL);
const fetchingStatus = ref("loading");

// Computed Props
const currentView = computed(() => {
  if (activeView.value.default) {
    return {
      ...activeView.value,
      name: activeView.value.name,
    };
  }

  return activeView.value;
});

// Functions
const handleUpdateFetchingStatus = (value) => {
  fetchingStatus.value = value;
};

const handleReset = () => {
  if (maxSmall.value) {
    emitter.emit("reset-filters");
  } else {
    viewsBar.value.reset();
  }
};

const handleReload = () => {
  window.location.reload();
};

const handleSelectView = (view) => {
  handleSwitchView(view);
  activeView.value = view;

  if (!view.default) {
    const secondNonDefaultItemIndex = views.value.findIndex(
      (v, index) =>
        !v.default && views.value.findIndex((w) => !w.default) === index - 1
    );
    const viewIndex = views.value.findIndex((v) => v.id === view.id);

    if (
      secondNonDefaultItemIndex !== -1 &&
      secondNonDefaultItemIndex <= viewIndex
    ) {
      [views.value[secondNonDefaultItemIndex], views.value[viewIndex]] = [
        views.value[viewIndex],
        views.value[secondNonDefaultItemIndex],
      ];
    }

    handleInitialiseFilters(view.filters);
    return;
  }

  const defaultFilters = view.filters.getActiveFilterKeys();
  handleInitialiseFilters(view.filters, defaultFilters);
};

const handleShowDetail = (file) => {
  router.visit(route("files.show", [file]), {
    preserveState: true,
    preserveScroll: true,
  });
};

const handleCloseFileDetails = () => {
  router.visit(route("files.index"), {
    preserveState: true,
    preserveScroll: true,
  });
};

const handleDeleteView = (viewId) => {
  views.value = views.value.filter((item) => item.id !== viewId);
};

const handleAddView = (view) => {
  view.default = false;
  views.value.push(view);
  handleSelectView(view);
};

const handleRenameView = (view) => {
  views.value[views.value.findIndex((item) => item.id === view.id)] = view;
};

const handleUpdateView = (view) => {
  activeView.value = view;
  views.value[views.value.findIndex((item) => item.id === view.id)] = view;
};

const redirectToTasks = () => {
  toast.info(t(`toasts.no_permission.files.read`));
  router.visit(route("tasks.index"));
};

// Watchers
watch(activeView, () => {
  emitter.emit("clear-data-table-selection");
});

watch(fetchingStatus, () => {
  handleUpdateLoading(fetchingStatus.value !== "loaded");
});

watch(
  () => globalFilterValues.value.tenant,
  () => {
    handleSelectView(DefaultView.ALL);
  }
);

watch(
  [globalFilterValues, buildings],
  () => {
    if (
      globalFilterValues.value.tenant &&
      !hasPermission(
        permissions.readDocument,
        globalFilterValues.value.tenant[0]
      )
    ) {
      redirectToTasks();
    } else if (
      !globalFilterValues.value.tenant &&
      buildings.value?.every(
        (building) => !hasPermission(permissions.readDocument, building.id)
      )
    ) {
      redirectToTasks();
    }
  },
  {
    deep: true,
  }
);

// Lifecycle Hooks
onMounted(() => {
  if (!isLoading.value) {
    views.value = defaultViews.concat(customViews.value);
  }

  watch(isLoading, (newValue) => {
    if (!newValue) {
      views.value = defaultViews.concat(customViews.value);
    }
  });
});

onUnmounted(() => {
  handleClearFilters();
  handleSelectView(DefaultView.ALL);
});
</script>
